<template>
  <div class="mt-2">
    <!-- Filters Row -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="d-flex flex-wrap gap-2 align-items-center">
          <div class="d-flex align-items-center gap-1">
            <select
              class="form-select form-select-sm"
              v-model="dateFilter"
              @change="onDateFilterChanged"
              :disabled="loading"
              style="width: auto; min-width: 140px;"
            >
              <option value="all">All Time</option>
              <option value="lastWeek">Last Week</option>
              <option value="currentWeek">Current Week</option>
              <option value="lastMonth">Last Month</option>
              <option value="currentMonth">Current Month</option>
              <option value="custom">Custom</option>
            </select>
            <div v-if="dateFilter === 'custom'" class="position-relative">
              <Datepicker
                v-model="dates"
                :range="true"
                :clearable="false"
                :enableTimePicker="false"
                :disabled="loading"
                @update:model-value="onCustomDatesChanged"
                class="form-control-sm"
                style="width: 200px;"
              />
            </div>
          </div>
          <select
            class="form-select form-select-sm"
            v-model="filters.customerId"
            :disabled="loading"
            style="width: 140px;"
          >
            <option value="">All Customers</option>
            <option v-for="customerId in uniqueCustomerIds" :key="customerId" :value="customerId">
              {{ getCustomerName(customerId) || customerId }}
            </option>
          </select>
          <select
            class="form-select form-select-sm"
            v-model="filters.userId"
            :disabled="loading"
            style="width: 160px;"
          >
            <option value="">All Users</option>
            <option v-for="userId in uniqueUserIds" :key="userId" :value="userId">
              {{ getUserName(userId) || userId }}
            </option>
          </select>
          <button 
            class="btn btn-outline-secondary btn-sm" 
            @click="clearFilters"
            :disabled="loading || !hasActiveFilters"
          >
            Clear
          </button>
          
          <!-- Include Internal Users Checkbox -->
          <div class="form-check ms-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              id="includeInternalUsers"
              v-model="includeInternalUsers"
              :disabled="loading"
            >
            <label class="form-check-label" for="includeInternalUsers">
              Include StreetMetrics users
            </label>
          </div>

          <!-- Smart Search version toggle -->
          <div class="btn-group ms-auto" role="group" aria-label="Smart Search version">
            <button
              type="button"
              class="btn btn-sm"
              :class="dataVersion === '1.0' ? 'btn-primary' : 'btn-outline-primary'"
              :disabled="loading"
              @click="dataVersion = '1.0'"
            >
              1.0
            </button>
            <button
              type="button"
              class="btn btn-sm"
              :class="dataVersion === '2.0' ? 'btn-primary' : 'btn-outline-primary'"
              :disabled="loading"
              @click="dataVersion = '2.0'"
            >
              2.0
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center py-5">
      <div class="spinner-border text-primary mb-2" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div class="text-primary">Loading analytics...</div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <i class="fas fa-exclamation-triangle fa-2x text-warning mb-3"></i>
      <h5 class="text-muted mb-3">Failed to load analytics</h5>
      <p class="text-muted mb-3">{{ error }}</p>
      <button class="btn btn-outline-primary" @click="loadAnalytics">
        <i class="fas fa-refresh me-2"></i>
        Retry
      </button>
    </div>

    <!-- Analytics Content -->
    <div v-else-if="analyticsData">

          <!-- Summary Metrics Cards -->
          <div class="d-flex flex-wrap gap-3 mb-4">
            <div class="stat-card conversations-interactions">
              <div class="dual-stats">
                <div class="dual-item">
                  <div class="dual-value">
                    {{ formatLargeNumber(analyticsData.summary.totalConversations) }}
                  </div>
                  <div class="dual-type">Conversations</div>
                </div>
                <div class="dual-divider"></div>
                <div class="dual-item">
                  <div class="dual-value">
                    {{ formatLargeNumber(analyticsData.summary.totalInteractions) }}
                  </div>
                  <div class="dual-type">Interactions</div>
                </div>
              </div>
              <div class="stat-label">Activity</div>
            </div>
            
            <div class="stat-card users-customers">
              <div class="dual-stats">
                <div class="dual-item">
                  <div class="dual-value">{{ formatNumber(analyticsData.summary.uniqueCustomers) }}</div>
                  <div class="dual-type">Customers</div>
                </div>
                <div class="dual-divider"></div>
                <div class="dual-item">
                  <div class="dual-value">{{ formatNumber(analyticsData.summary.uniqueUsers) }}</div>
                  <div class="dual-type">Users</div>
                </div>
              </div>
              <div class="stat-label">User Base</div>
            </div>
            
            <div class="stat-card performance">
              <div class="dual-stats">
                <div class="dual-item">
                  <div class="dual-value">{{ formatResponseTime(analyticsData.summary.avgResponseTime) }}</div>
                  <div class="dual-type">Avg. Response Time</div>
                </div>
                <div class="dual-divider"></div>
                <div class="dual-item">
                  <div class="dual-value css-tooltip"
                       :data-tooltip="formatNumber(analyticsData.summary.totalTokens)">
                    {{ formatLargeNumber(analyticsData.summary.totalTokens) }}
                  </div>
                  <div class="dual-type">Total Tokens</div>
                </div>
              </div>
              <div class="stat-label">Performance</div>
            </div>
            

          </div>

          <!-- Time Series Chart Section -->
          <div class="card mt-4">
            <div class="card-body">
              <!-- Chart View Toggle Buttons -->
              <div class="d-flex gap-2 mb-3 align-items-center">
                <button 
                  class="btn btn-sm"
                  :class="chartView === 'activity' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setChartView('activity')"
                >
                  Activity
                </button>
                <button 
                  class="btn btn-sm"
                  :class="chartView === 'customers' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setChartView('customers')"
                  :disabled="isCustomerViewDisabled"
                >
                  Customers
                </button>
                <button 
                  class="btn btn-sm"
                  :class="chartView === 'users' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setChartView('users')"
                  :disabled="isUserViewDisabled"
                >
                  Users
                </button>
                
                <!-- Metric Dropdown - only shown for customers/users views -->
                <div v-if="chartView === 'customers' || chartView === 'users'" class="ms-3">
                  <select 
                    class="form-select form-select-sm" 
                    v-model="selectedMetric"
                    :disabled="loading"
                    style="width: auto; min-width: 140px;"
                  >
                    <option value="conversations">Conversations</option>
                    <option value="interactions">Interactions</option>
                  </select>
                </div>
                
                <!-- Toggle All Button - only shown for customers/users views -->
                <div v-if="chartView === 'customers' || chartView === 'users'" class="ms-3">
                  <button 
                    class="btn btn-outline-secondary btn-sm"
                    @click="toggleAllSeries"
                    :disabled="loading"
                    :title="allSeriesHidden ? 'Show all series' : 'Hide all series'"
                  >
                    <font-awesome-icon :icon="allSeriesHidden ? 'eye' : 'eye-slash'" />
                    {{ allSeriesHidden ? 'Show All' : 'Hide All' }}
                  </button>
                </div>
              </div>

              <!-- Time Series Chart -->
              <div class="chart-container">
                <apexchart 
                  ref="apexchart"
                  type="line" 
                  :options="timeSeriesChartOptions" 
                  :series="timeSeriesChartSeries"
                  height="400">
                </apexchart>
              </div>
            </div>
          </div>
        </div>
  </div>
</template>

<script>
import InsightsService from '../../services/InsightsService.js';
import AppUtil from '../../utils/AppUtil.js';
import Datepicker from 'vue3-date-time-picker';
import dayjs from 'dayjs';

export default {
  name: 'AnalyticsDashboard',
  components: {
    Datepicker,
  },
  props: {
    viewModel: {
      type: Object,
      required: true
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      analyticsDataV1: null,
      analyticsDataV2: null,
      dataVersion: '1.0', // '1.0' | '2.0'
      chartView: 'activity', // 'activity', 'customers', 'users'
      selectedMetric: 'conversations', // 'conversations', 'interactions'
      includeInternalUsers: false, // default to excluding internal users
      filters: {
        customerId: '',
        userId: '',
      },
      dateFilter: 'all',
      startDate: null,
      endDate: null,
      dates: [],
      model: streetmetrics.model,
      insightsService: new InsightsService(),
      appUtil: new AppUtil(),
      loadAnalyticsTimeout: null,
      allSeriesHidden: false // track if all series are currently hidden
    }
  },
  mounted() {
    this.loadAnalytics();
  },
  
  watch: {
    'filters.customerId'(newCustomerId, oldCustomerId) {
      if (oldCustomerId && newCustomerId !== oldCustomerId && this.filters.userId) {
        this.filters.userId = '';
      }
      this.chartView = 'activity';
      this.debouncedLoadAnalytics();
    },
    'filters.userId'() {
      this.chartView = 'activity';
      this.debouncedLoadAnalytics();
    },
    dateFilter() {
      this.chartView = 'activity';
      this.debouncedLoadAnalytics();
    },
    startDate() {
      this.debouncedLoadAnalytics();
    },
    endDate() {
      this.debouncedLoadAnalytics();
    },
    includeInternalUsers() {
      this.chartView = 'activity';
      this.debouncedLoadAnalytics();
    },
    dataVersion() {
      this.chartView = 'activity';
      this.allSeriesHidden = false;
    },
  },
  
  beforeUnmount() {
    if (this.loadAnalyticsTimeout) {
      clearTimeout(this.loadAnalyticsTimeout);
    }
  },
  
  computed: {
    analyticsData() {
      return this.dataVersion === '2.0' ? this.analyticsDataV2 : this.analyticsDataV1;
    },

    uniqueCustomerIds() {
      if (!this.analyticsData?.customerIds) return [];
      return this.analyticsData.customerIds;
    },
    
    uniqueUserIds() {
      if (!this.analyticsData?.userIds) return [];
      return this.analyticsData.userIds;
    },
    
    hasActiveFilters() {
      return this.filters.customerId || this.filters.userId || this.dateFilter !== 'all';
    },
    
    isCustomerViewDisabled() {
      return !!this.filters.customerId || !!this.filters.userId;
    },
    
    isUserViewDisabled() {
      return !!this.filters.userId;
    },
    
    timeSeriesChartSeries() {
      if (!this.analyticsData?.timeSeries) return [];
      
      if (this.chartView === 'activity') {
        return [
          {
            name: 'Conversations',
            data: this.analyticsData.timeSeries.map(point => ({
              x: dayjs(point.date).valueOf(),
              y: point.conversations
            }))
          },
          {
            name: 'Interactions', 
            data: this.analyticsData.timeSeries.map(point => ({
              x: dayjs(point.date).valueOf(),
              y: point.interactions
            }))
          }
        ];
      } else if (this.chartView === 'customers') {
        const customerIds = this.calculateEntityTotals(point => point.customers);
        return this.createEntitySeries(
          customerIds,
          customerId => this.getCustomerName(customerId),
          (point, customerId) => {
            const customer = point.customers.find(c => c.customerId === customerId);
            return customer ? this.getMetricValue(customer) : 0;
          }
        );
      } else if (this.chartView === 'users') {
        const userIds = this.calculateEntityTotals(point => {
          const allUsers = [];
          point.customers.forEach(customer => {
            allUsers.push(...customer.users);
          });
          return allUsers;
        });
        return this.createEntitySeries(
          userIds,
          userId => this.getUserName(userId),
          (point, userId) => {
            let userMetricValue = 0;
            point.customers.forEach(customer => {
              const user = customer.users.find(u => u.userId === userId);
              if (user) {
                userMetricValue += this.getMetricValue(user);
              }
            });
            return userMetricValue;
          }
        );
      }
      
      return [];
    },
    
    timeSeriesChartOptions() {
      return {
        chart: {
          type: 'line',
          toolbar: { show: true },
          zoom: { enabled: true }
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        xaxis: {
          type: 'datetime',
          labels: {
            datetimeUTC: false
          }
        },
        yaxis: {
          labels: {
            formatter: (value) => this.formatLargeNumber(value)
          }
        },
        tooltip: {
          x: {
            format: 'MMM dd, yyyy'
          },
          y: {
            formatter: (value) => this.formatLargeNumber(value)
          }
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'center',
          itemMargin: {
            horizontal: 10,
            vertical: 5
          }
        },
        grid: {
          borderColor: '#e9ecef'
        },
        colors: [
          '#0d6efd', '#17a2b8', '#28a745', '#ffc107', '#dc3545',
          '#6f42c1', '#fd7e14', '#20c997', '#e83e8c', '#6c757d',
          '#0dcaf0', '#198754', '#6610f2', '#d63384', '#8b5cf6',
          '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5a2b',
          '#6366f1', '#ec4899', '#14b8a6', '#f97316', '#84cc16',
          '#eab308', '#a855f7', '#3b82f6', '#22c55e', '#f43f5e',
          '#7c3aed', '#059669', '#dc2626', '#ea580c', '#0891b2'
        ]
      };
    }
  },
  
  methods: {
    setChartView(view) {
      this.chartView = view;
      this.allSeriesHidden = false; // Reset state when changing views
    },
    
    toggleAllSeries() {
      // Wait for next tick to ensure chart is rendered
      this.$nextTick(() => {
        const chartElement = this.$refs.apexchart;
        if (chartElement && chartElement.chart) {
          const chart = chartElement.chart;
          const seriesNames = chart.w.globals.seriesNames;
          
          if (this.allSeriesHidden) {
            seriesNames.forEach(seriesName => {
              chart.showSeries(seriesName);
            });
            this.allSeriesHidden = false;
          } else {
            seriesNames.forEach(seriesName => {
              chart.hideSeries(seriesName);
            });
            this.allSeriesHidden = true;
          }
        }
      });
    },
    
    getMetricValue(entity) {
      return this.selectedMetric === 'conversations' ? entity.conversations : entity.interactions;
    },
    
    calculateEntityTotals(extractEntitiesFn) {
      const totals = new Map();
      this.analyticsData.timeSeries.forEach(point => {
        const entities = extractEntitiesFn(point);
        entities.forEach(entity => {
          const entityId = entity.customerId || entity.userId;
          const current = totals.get(entityId) || 0;
          totals.set(entityId, current + this.getMetricValue(entity));
        });
      });
      
      return Array.from(totals.entries())
        .sort((a, b) => b[1] - a[1])
        .map(entry => entry[0]);
    },
    
    createEntitySeries(entityIds, getNameFn, getEntityValueFn) {
      // First pass: count all names to identify duplicates
      const nameCounts = {};
      entityIds.forEach(entityId => {
        const baseName = getNameFn(entityId) || `${this.chartView === 'customers' ? 'Customer' : 'User'} ${entityId}`;
        nameCounts[baseName] = (nameCounts[baseName] || 0) + 1;
      });
      
      // Second pass: create series with unique names
      return entityIds.map(entityId => {
        let baseName = getNameFn(entityId) || `${this.chartView === 'customers' ? 'Customer' : 'User'} ${entityId}`;
        
        // If this is a users view and the name appears multiple times, add ID to all instances
        if (this.chartView === 'users' && nameCounts[baseName] > 1) {
          baseName = `${baseName} (${entityId})`;
        }
        
        return {
          name: baseName,
          data: this.analyticsData.timeSeries.map(point => ({
            x: dayjs(point.date).valueOf(),
            y: getEntityValueFn(point, entityId)
          }))
        };
      });
    },
    
    debouncedLoadAnalytics() {
      if (this.loadAnalyticsTimeout) {
        clearTimeout(this.loadAnalyticsTimeout);
      }
      
      this.loadAnalyticsTimeout = setTimeout(() => {
        this.loadAnalytics();
      }, 100);
    },
    
    async loadAnalytics() {
      this.loading = true;
      this.error = null;
      
      try {
        const filterParams = {};
        
        if (this.filters.customerId) {
          filterParams.customerId = this.filters.customerId;
        }
        
        if (this.filters.userId) {
          filterParams.userId = this.filters.userId;
        }
        
        if (this.startDate && this.endDate) {
          filterParams.startDate = this.startDate.toISOString();
          filterParams.endDate = this.endDate.toISOString();
        }
        
        filterParams.includeInternalUsers = this.includeInternalUsers;
        
        const [v1Response, v2Response] = await Promise.all([
          this.insightsService.getAdminConversationsAnalytics(filterParams),
          this.insightsService.getAdminConversationsAnalyticsV2(filterParams),
        ]);
        this.analyticsDataV1 = v1Response;
        this.analyticsDataV2 = v2Response;
      } catch (error) {
        console.error('Error loading analytics:', error);
        this.error = error.message || 'Failed to load analytics data';
      } finally {
        this.loading = false;
      }
    },

    onDateFilterChanged() {
      if (this.dateFilter === 'all') {
        this.startDate = null;
        this.endDate = null;
        this.dates = [];
      } else if (this.dateFilter !== 'custom') {
        const startAndEndDates = this.appUtil.getFilterDates(this.dateFilter);
        this.startDate = startAndEndDates.start;
        this.endDate = startAndEndDates.end;
        this.dates = [startAndEndDates.start, startAndEndDates.end];
      }
    },
    
    onCustomDatesChanged(newDates) {
      if (newDates && newDates.length === 2) {
        this.startDate = newDates[0];
        this.endDate = newDates[1];
        this.dates = [...newDates];
      }
    },

    clearFilters() {
      this.filters = {
        customerId: '',
        userId: '',
      };
      this.dateFilter = 'all';
      this.startDate = null;
      this.endDate = null;
      this.dates = [];
    },
    
    formatNumber(value) {
      if (value == null) return '0';
      return new Intl.NumberFormat().format(value);
    },
    
    formatLargeNumber(num) {
      if (num == null || num === 0) return '0';
      
      const thresholds = [
        { threshold: 1_000_000_000_000, suffix: 'T' },
        { threshold: 1_000_000_000, suffix: 'B' },
        { threshold: 1_000_000, suffix: 'M' },
        { threshold: 1_000, suffix: 'K' },
      ];

      for (const { threshold, suffix } of thresholds) {
        if (num >= threshold) {
          const value = Math.round(num / threshold);
          return value.toString() + suffix;
        }
      }

      return num.toString();
    },
    
    formatPercentage(value) {
      if (value == null) return '0%';
      return `${Math.round(value * 100)}%`;
    },
    
    formatResponseTime(milliseconds) {
      if (milliseconds == null) return '0ms';
      if (milliseconds < 1000) {
        return `${Math.round(milliseconds)}ms`;
      }
      
      const seconds = milliseconds / 1000;
      if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
      }
      
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    },
    
    getUserName(userId) {
      if (!userId || !this.model.users) return null;
      const user = this.model.users.find(u => u.userId === userId);
      return user ? user.displayName : null;
    },
    
    getCustomerName(customerId) {
      if (!customerId || !this.model.customers) return null;
      const customer = this.model.customers.find(c => c.customerId === customerId);
      return customer ? customer.customerName : null;
    },
    
    getUserCustomerName(userId) {
      if (!userId || !this.model.users) return null;
      const user = this.model.users.find(u => u.userId === userId);
      if (!user || !user.customer) return null;
      return user.customer.customerName || null;
    }
  }
}
</script>

<style scoped>
.card {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.d-flex .stat-card {
  flex: 1;
  min-width: 250px;
}

.stat-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1.25rem;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  transition: all 0.2s ease;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.85rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.dual-stats {
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-bottom: 0.75rem;
}

.dual-item {
  text-align: center;
  flex: 1;
}

.dual-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  line-height: 1.2;
}

.dual-type {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: #6c757d;
}

.dual-divider {
  width: 1px;
  height: 35px;
  background-color: #dee2e6;
  margin: 0 0.75rem;
}

.stat-card.conversations-interactions .dual-value {
  color: #0d6efd;
}

.stat-card.users-customers .dual-value {
  color: #17a2b8;
}

.stat-card.performance .dual-value {
  color: #28a745;
}

.stat-card.feedback-combined .dual-item.positive .dual-value {
  color: #28a745;
}

.stat-card.feedback-combined .dual-item.negative .dual-value {
  color: #dc3545;
}

.chart-container {
  background-color: #fafafa;
  border-radius: 0.375rem;
  padding: 1rem;
}

.table-container {
  max-height: 400px;
  overflow-y: auto;
}

@media (max-width: 768px) {
  .d-flex .stat-card {
    min-width: 200px;
    padding: 1rem;
  }
  
  .dual-value {
    font-size: 1.25rem;
  }
  
  .dual-type {
    font-size: 0.65rem;
  }
  
  .dual-divider {
    height: 30px;
    margin: 0 0.5rem;
  }
  
  .stat-label {
    font-size: 0.75rem;
  }
  
  .border-start {
    border-left: none !important;
    border-top: 1px solid #dee2e6 !important;
    margin-top: 2rem;
    padding-top: 2rem;
  }
}

.css-tooltip {
  position: relative;
  cursor: help;
}

.css-tooltip::before {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: normal;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0.2s;
  z-index: 1000;
  pointer-events: none;
  margin-bottom: 8px;
}

.css-tooltip::after {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: rgba(0, 0, 0, 0.9);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0.2s;
  z-index: 1000;
  pointer-events: none;
  margin-bottom: 3px;
}

.css-tooltip:hover::before,
.css-tooltip:hover::after {
  opacity: 1;
  visibility: visible;
}
</style>
